// src/components/dialogs/UserDialog.tsx

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Dialog, DialogContent, TextField, Typography, DialogActions,
  DialogTitle, Stack, InputAdornment, IconButton, FormControl, InputLabel,
  Select, MenuItem, Alert, CircularProgress
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff, PersonAdd } from '@mui/icons-material';

// Tus hooks y constantes
import { useUserCore } from '../../../core/modules/user';
import { useGlobalCore } from '../../../core/modules/global';
import { CREATE_USER_DIALOG } from '../../../core/constants/dialog';
import { useCompanyCore } from '../../../core/modules/company';
import { CompanyRole } from '../../../core/modules/user/interfaces/role';
import { PostUserPayloadRequest } from '../../../core/modules/user/interfaces/post-user';

// Definición del tipo para los datos del formulario
interface FormData {
  name: string;
  email: string;
  password: string;
  phone_number?: string;
  country?: string;
  city?: string;
  companyId: string;
  role: CompanyRole;
}

const schema = yup.object().shape({
  name: yup.string().required('Debes ingresar un nombre'),
  email: yup.string().email('El formato del email no es correcto').required('Debes ingresar un email'),
  password: yup.string().min(8, 'La contraseña debe tener al menos 8 caracteres').required('Debes ingresar una contraseña'),
  phone: yup.string(),
  country: yup.string(),
  city: yup.string(),
  companyId: yup.string().required('Debes seleccionar una empresa'),
  role: yup.string().oneOf(Object.values(CompanyRole)).required('Debes seleccionar un rol'),
});

const defaultValues: FormData = {
  name: '',
  email: '',
  password: '',
  phone_number: '',
  country: '',
  city: '',
  companyId: '',
  role: CompanyRole.USER,
};

const UserDialog = () => {
  const { t } = useTranslation();
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { getUsers, postUser } = useUserCore();
  const { getCompanies, companies } = useCompanyCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, errors, isDirty } = formState;

  // Cargar las compañías cuando el diálogo se abre
  useEffect(() => {
    if (dialog === CREATE_USER_DIALOG) {
      getCompanies({});
      reset(defaultValues);
    }
  }, [dialog, getCompanies, reset]);

  const handleClose = () => {
    setApiError(null);
    closeDialog();
  };

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (formData) => {
      setIsSubmitting(true);
      setApiError(null);

      const payload: PostUserPayloadRequest = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone_number: formData.phone_number,
        country: formData.country,
        city: formData.city,
        companyId: formData.companyId,
        role: formData.role,
      };

      await postUser(payload, 
        () => { // onSuccess
          showSnackbar({ message: 'Usuario creado exitosamente.', options: { variant: 'success' } });
          getUsers({}); // Recargar la lista de usuarios
          handleClose();
        },
        (error: any) => { // onFailure
          const message = error.response?.data?.detail || 'Ocurrió un error al crear el usuario.';
          setApiError(message);
        }
      );
      
      setIsSubmitting(false);
    },
    [postUser, getUsers, showSnackbar, handleClose]
  );

  return (
    <Dialog open={dialog === CREATE_USER_DIALOG} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h5" component="div" className="font-bold flex items-center gap-2">
          <PersonAdd />
          {t('users.modal.create.title')}
        </Typography>
      </DialogTitle>
      
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3} className="pt-2">
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label={t('label.name')} error={!!errors.name} helperText={errors.name?.message} required fullWidth autoFocus />
            )} />

            <Controller name="email" control={control} render={({ field }) => (
              <TextField {...field} label="Email" type="email" error={!!errors.email} helperText={errors.email?.message} required fullWidth />
            )} />

            <Controller name="password" control={control} render={({ field }) => (
              <TextField {...field} label={t('label.password')} type={showPassword ? 'text' : 'password'} error={!!errors.password} helperText={errors.password?.message} required fullWidth 
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )} />

            <FormControl fullWidth error={!!errors.companyId}>
              <InputLabel id="company-select-label">Empresa *</InputLabel>
              <Controller name="companyId" control={control} render={({ field }) => (
                <Select {...field} labelId="company-select-label" label="Empresa *">
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                  ))}
                </Select>
              )} />
              {errors.companyId && <Typography variant="caption" color="error">{errors.companyId.message}</Typography>}
            </FormControl>

            {/* --- NUEVO CAMPO PARA EL ROL --- */}
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Rol *</InputLabel>
              <Controller name="role" control={control} render={({ field }) => (
                <Select {...field} labelId="role-select-label" label="Rol *">
                  <MenuItem value={CompanyRole.USER}>{CompanyRole.USER}</MenuItem>
                  <MenuItem value={CompanyRole.COMPANY_ADMIN}>{CompanyRole.COMPANY_ADMIN}</MenuItem>
                  <MenuItem value={CompanyRole.TOMADOR_INVENTARIO}>{CompanyRole.TOMADOR_INVENTARIO}</MenuItem>
                </Select>
              )} />
            </FormControl>

          </Stack>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Creando...' : 'Crear Usuario'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog;